import React, { useState, useCallback, useEffect } from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import * as _ from 'lodash';
import SearchBox from 'components/general/SearchBox';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FETCH_CONSOLIDATED_INVOICE,
  DOWNLOAD_FETCH_CONSOLIDATED_INVOICE,
  FETCH_CONSOLIDATED_INVOICE_EXCEL,
} from 'actions/dataReport';
import moment from 'moment';
import ReactPagination from 'components/general/ReactPagination';
import fileDownload from 'js-file-download';
import { BsDownload } from 'react-icons/bs';
import Button from 'components/inputs/Button';

const FleetConsolidatedInvoices = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  const isLoader = useSelector((state) => state.alldatareport.isLoading);
  const reportLoader = useSelector((state) => state.alldatareport.reportLoader);
  const allDataList = useSelector((state) => state.alldatareport.consolidatedInvoice);
  const page = useSelector((state) => state.alldatareport.page);
  const limit = useSelector((state) => state.alldatareport.limit);
  const totalPages = useSelector((state) => state.alldatareport.totalPages);
  const total = useSelector((state) => state.alldatareport.total);

  const currentYear = moment().year();
  const years = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const fetchConsolidatedInvoices = useCallback(
    (data = {}) => {
      if (!selectedYear || !selectedMonth) return;

      const payload = {
        year: parseInt(selectedYear),
        month: parseInt(selectedMonth),
        page: data.page || 1,
        limit: data.limit || limit,
        isFleetInvoice: true,
      };

      if (searchText) {
        payload.search = searchText;
      }

      dispatch({ type: FETCH_CONSOLIDATED_INVOICE.REQUEST, payload });
    },
    [searchText, selectedYear, selectedMonth, limit, dispatch]
  );

  useEffect(() => {
    if (selectedYear && selectedMonth) {
      fetchConsolidatedInvoices({ page: 1, limit });
    }
  }, [searchText, selectedYear, selectedMonth]);

  const handleSearch = () => {
    if (selectedYear && selectedMonth) {
      fetchConsolidatedInvoices({});
    }
  };

  const handleDownloadInvoice = (id) => {
    setDownloadingId(id);
    dispatch({
      type: DOWNLOAD_FETCH_CONSOLIDATED_INVOICE.REQUEST,
      payload: { id },
      cb: (res) => {
        setDownloadingId(null);
        if (res && _.get(res, 'status') === 200) {
          fileDownload(res.data, 'Invoice.pdf');
        }
      },
    });
  };

  const handlePageClick = useCallback(
    (page) => {
      if (!selectedYear || !selectedMonth) return;
      fetchConsolidatedInvoices({ page: page.selected + 1 });
    },
    [fetchConsolidatedInvoices, selectedYear, selectedMonth]
  );

  const handleDownloadDateRange = () => {
    if (!selectedYear || !selectedMonth) return;

    const payload = {
      year: parseInt(selectedYear),
      month: parseInt(selectedMonth),
      excel: true,
      isFleetInvoice: true,
    };
    if (searchText) {
      payload.search = searchText;
    }

    dispatch({
      type: FETCH_CONSOLIDATED_INVOICE_EXCEL.REQUEST,
      payload,
      cb: (res) => {
        if (res && res.status === 200) {
          fileDownload(res.data, 'Fleet-Consolidated-Invoice.xlsx');
        }
      },
    });
  };

  const selectStyle = {
    height: '50px',
    borderRadius: '15px',
    background: '#182532',
    border: '1px solid #1b2a38',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '0.813rem',
    padding: '0.5rem 1rem',
  };

  return (
    <div>
      <Row className="data-report__row">
        <Col xl={5} lg={5} md={5} sm={12}>
          <SearchBox
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
        </Col>
        <Col xl={2} lg={2} md={2} sm={4}>
          <select
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(e.target.value);
              setSelectedMonth('');
            }}
            className="form-select"
            style={selectStyle}
          >
            <option value="">{t('consolidatedInvoiceReport.selectYear')}</option>
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </Col>
        <Col xl={2} lg={2} md={2} sm={4}>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="form-select"
            disabled={!selectedYear}
            style={selectStyle}
          >
            <option value="">{t('consolidatedInvoiceReport.selectMonth')}</option>
            {months.map((month) => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
        </Col>
        <Col xl={'auto'} md={'auto'} className="excel-icon--block" onClick={handleDownloadDateRange}>
          {isLoader ? (
            <div className="loader--block">
              <Spinner as="span" animation="border" size="sm" role="status" />
            </div>
          ) : (
            <div className="report-excel-icon">
              <BsDownload title="Download" size={28} color={'#3c7cdd'} />
            </div>
          )}
        </Col>
      </Row>

      <div className="countryList-table">
        <div className="table-responsive">
          <table className="record-list-table">
            <thead>
              <tr>
                <th><span>{t('consolidatedInvoiceReport.srNo')}</span></th>
                <th><span>{t('consolidatedInvoiceReport.state')}</span></th>
                <th><span>{t('consolidatedInvoiceReport.fleetName')}</span></th>
                <th><span>{t('consolidatedInvoiceReport.invoiceNo')}</span></th>
                <th><span>{t('consolidatedInvoiceReport.subTotal')}</span></th>
                <th><span>{t('consolidatedInvoiceReport.gst')}</span></th>
                <th><span>{t('consolidatedInvoiceReport.gstType')}</span></th>
                <th><span>{t('consolidatedInvoiceReport.totalAmount')}</span></th>
                <th className="action-col"><span>{t('consolidatedInvoiceReport.action')}</span></th>
              </tr>
            </thead>
            <tbody>
              {reportLoader ? (
                <tr>
                  <td colSpan={9} className="border-0">
                    <div className="loader--block">
                      <Spinner as="span" animation="border" size="sm" role="status" />
                      <span className="ps-2">{t('placeHolder.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : _.isEmpty(allDataList) ? (
                <tr>
                  <td colSpan={9} className="border-0">
                    <div className="empty-data-block">{t('No data Found')}</div>
                  </td>
                </tr>
              ) : (
                !(reportLoader || _.isUndefined(reportLoader)) &&
                _.map(allDataList, (item, key) => {
                  const serialNo = (page - 1) * limit + key + 1;
                  return (
                    <tr key={`fleet-consolidated-${key}`}>
                      <td>{serialNo}</td>
                      <td>{_.get(item, 'state', '')}</td>
                      <td>{_.get(item, 'user.name', '')}</td>
                      <td>{_.get(item, 'invoice_no', '')}</td>
                      <td>{_.get(item, 'subtotal', '')}</td>
                      <td>{_.get(item, 'gst', '')}</td>
                      <td>{_.get(item, 'gst_type', '')}</td>
                      <td>{_.get(item, 'total_amount', '')}</td>
                      <td>
                        {downloadingId === item.id ? (
                          <div className="loader--block">
                            <Spinner as="span" animation="border" size="sm" role="status" />
                          </div>
                        ) : (
                          <Button
                            onClick={() => handleDownloadInvoice(item.id)}
                          >
                            {t('consolidatedInvoiceReport.download')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {!(isLoader || _.isUndefined(isLoader)) && !_.isEmpty(allDataList) && (
        <ReactPagination
          currentPage={page}
          limit={limit}
          total={total}
          handlePageClick={(pageVal) => handlePageClick(pageVal)}
          totalPages={totalPages}
          marginPagesDisplayed={1}
        />
      )}
    </div>
  );
};

export default FleetConsolidatedInvoices;
